import { FC, useState } from 'react'

import styled from 'styled-components'

import { Switch } from 'src/Components/Switch'
import { SimpleLabel } from 'src/Components/SimpleLabel'
import { QuestionButton } from 'src/Components/QuestionButton'

import { DisableAutoRenewModal } from './DisableAutoRenewModal'

const ActiveContainer = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 24px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  box-shadow: ${({ theme }) => theme.shadow.card};
`

const OffContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  width: 100%;
  padding: 24px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.brandGreen};
`

const Content = styled.div`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`

const Headline = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  width: 100%;
`

const Title = styled.p`
  flex: 1 0 0;
  min-width: 0;
  margin: 0;
  font-family: 'Segoe UI';
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
  word-break: break-word;
`

const OffTitleWrapper = styled.div`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  gap: 4px;
  align-items: center;
`

const OffTitle = styled.p`
  margin: 0;
  font-family: 'Segoe UI';
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
  white-space: nowrap;
`

const Description = styled.p`
  width: 100%;
  margin: 0;
  font-family: 'Segoe UI';
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
  word-break: break-word;
`

const Highlight = styled.span`
  color: ${({ theme }) => theme.color.brandGreen};
`

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2px 0;
  flex-shrink: 0;
`

export interface CampaignDetailsAutoRenewBlockProps {
  isActive: boolean
  renewalDate?: string
  duration?: number
  onEnable: () => void
  onDisableConfirm: () => void
  className?: string
}

export const CampaignDetailsAutoRenewBlock: FC<
  CampaignDetailsAutoRenewBlockProps
> = ({
  isActive,
  renewalDate,
  duration,
  onEnable,
  onDisableConfirm,
  className,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const closeModal = (): void => setIsModalOpen(false)

  const confirmDisable = (): void => {
    closeModal()
    onDisableConfirm()
  }

  if (!isActive) {
    return (
      <OffContainer className={className}>
        <ToggleWrapper>
          <Switch
            checked={false}
            onChange={() => onEnable()}
            aria-label='Enable auto-renew'
          />
        </ToggleWrapper>
        <Content>
          <Headline>
            <OffTitleWrapper>
              <OffTitle>Auto-renew campaign</OffTitle>
              <SimpleLabel color='green'>Recommended</SimpleLabel>
            </OffTitleWrapper>
          </Headline>
          <Description>
            Turn on auto-renew to give Meta&apos;s algorithm more time to learn,
            optimize, and improve your campaign results over time.
            <br />
            Auto-renew can help reduce cost per conversion by{' '}
            <Highlight>Up to 23% over time 🚀</Highlight>
          </Description>
        </Content>
      </OffContainer>
    )
  }

  return (
    <>
      <ActiveContainer className={className}>
        <Content>
          <Headline>
            <Title>Auto-renew is active</Title>
            <QuestionButton
              type='button'
              aria-label='Manage auto-renew'
              onClick={() => setIsModalOpen(true)}
            />
          </Headline>
          <Description>
            Your campaign will continue automatically on {renewalDate} for{' '}
            {duration} days.
          </Description>
        </Content>
      </ActiveContainer>
      <DisableAutoRenewModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={confirmDisable}
      />
    </>
  )
}
